import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
  runTransaction,
} from '@firebase/firestore';
import { db } from './firebase';
import type {
  PlaceSuggestion,
  SuggestionCreateInput,
  SuggestionStatus,
  PlaceCreateInput,
  Place,
} from '../types';
import { isUserModerator } from './moderationService';

const SUGGESTIONS_COLLECTION = 'placeSuggestions';
const PLACES_COLLECTION = 'places';
const MODERATION_ACTIONS_COLLECTION = 'moderationActions';

/**
 * Create a new suggestion for an existing place
 */
export async function createSuggestion(
  placeId: string,
  placeName: string,
  changes: Partial<PlaceCreateInput>,
  userId: string,
  userName: string,
  userEmail?: string
): Promise<string> {
  // Verify the place exists
  const placeRef = doc(db, PLACES_COLLECTION, placeId);
  const placeSnap = await getDoc(placeRef);

  if (!placeSnap.exists()) {
    throw new Error('Ort nicht gefunden');
  }

  // Create the suggestion
  const docRef = await addDoc(collection(db, SUGGESTIONS_COLLECTION), {
    placeId,
    placeName,
    changes,
    suggestedBy: userId,
    suggestedByName: userName,
    suggestedByEmail: userEmail || null,
    status: 'pending' as SuggestionStatus,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
}

/**
 * Get a suggestion by ID
 */
export async function getSuggestionById(
  id: string
): Promise<PlaceSuggestion | null> {
  const docRef = doc(db, SUGGESTIONS_COLLECTION, id);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as PlaceSuggestion;
}

/**
 * Get all pending suggestions
 */
export async function getPendingSuggestions(): Promise<PlaceSuggestion[]> {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PlaceSuggestion[];
}

/**
 * Get count of pending suggestions
 */
export async function getPendingSuggestionsCount(): Promise<number> {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Get suggestions for a specific place
 */
export async function getSuggestionsForPlace(
  placeId: string
): Promise<PlaceSuggestion[]> {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('placeId', '==', placeId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PlaceSuggestion[];
}

/**
 * Get suggestions submitted by a specific user
 */
export async function getUserSuggestions(
  userId: string
): Promise<PlaceSuggestion[]> {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('suggestedBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as PlaceSuggestion[];
}

/**
 * Subscribe to pending suggestions (real-time)
 */
export function subscribeToPendingSuggestions(
  callback: (suggestions: PlaceSuggestion[]) => void
): Unsubscribe {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const suggestions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as PlaceSuggestion[];
    callback(suggestions);
  });
}

/**
 * Approve a suggestion (apply all changes to the place)
 */
export async function approveSuggestion(
  suggestionId: string,
  moderatorId: string
): Promise<void> {
  // Check if user is moderator
  const isModerator = await isUserModerator(moderatorId);
  if (!isModerator) {
    throw new Error('Nur Moderatoren koennen Vorschlaege genehmigen');
  }

  const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
  const suggestionSnap = await getDoc(suggestionRef);

  if (!suggestionSnap.exists()) {
    throw new Error('Vorschlag nicht gefunden');
  }

  const suggestion = suggestionSnap.data() as PlaceSuggestion;

  if (suggestion.status !== 'pending') {
    throw new Error('Vorschlag wurde bereits bearbeitet');
  }

  // Use transaction to update both suggestion and place
  await runTransaction(db, async (transaction) => {
    const placeRef = doc(db, PLACES_COLLECTION, suggestion.placeId);

    // Update the place with the suggested changes
    transaction.update(placeRef, {
      ...suggestion.changes,
      updatedAt: serverTimestamp(),
    });

    // Mark suggestion as approved
    transaction.update(suggestionRef, {
      status: 'approved' as SuggestionStatus,
      reviewedBy: moderatorId,
      reviewedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  // Log moderation action
  await addDoc(collection(db, MODERATION_ACTIONS_COLLECTION), {
    placeId: suggestion.placeId,
    suggestionId,
    moderatorId,
    action: 'approve_suggestion',
    timestamp: serverTimestamp(),
  });
}

/**
 * Approve a suggestion partially (only selected fields)
 */
export async function approveSuggestionPartially(
  suggestionId: string,
  moderatorId: string,
  approvedFields: string[],
  reviewNote?: string
): Promise<void> {
  // Check if user is moderator
  const isModerator = await isUserModerator(moderatorId);
  if (!isModerator) {
    throw new Error('Nur Moderatoren koennen Vorschlaege genehmigen');
  }

  const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
  const suggestionSnap = await getDoc(suggestionRef);

  if (!suggestionSnap.exists()) {
    throw new Error('Vorschlag nicht gefunden');
  }

  const suggestion = suggestionSnap.data() as PlaceSuggestion;

  if (suggestion.status !== 'pending') {
    throw new Error('Vorschlag wurde bereits bearbeitet');
  }

  // Filter changes to only include approved fields
  const approvedChanges: Partial<PlaceCreateInput> = {};
  for (const field of approvedFields) {
    if (field in suggestion.changes) {
      (approvedChanges as Record<string, unknown>)[field] = (
        suggestion.changes as Record<string, unknown>
      )[field];
    }
  }

  // Use transaction to update both suggestion and place
  await runTransaction(db, async (transaction) => {
    const placeRef = doc(db, PLACES_COLLECTION, suggestion.placeId);

    // Update the place with the approved changes only
    transaction.update(placeRef, {
      ...approvedChanges,
      updatedAt: serverTimestamp(),
    });

    // Mark suggestion as partially approved
    transaction.update(suggestionRef, {
      status: 'partial' as SuggestionStatus,
      approvedFields,
      reviewedBy: moderatorId,
      reviewedAt: serverTimestamp(),
      reviewNote: reviewNote || null,
      updatedAt: serverTimestamp(),
    });
  });

  // Log moderation action
  await addDoc(collection(db, MODERATION_ACTIONS_COLLECTION), {
    placeId: suggestion.placeId,
    suggestionId,
    moderatorId,
    action: 'partial_approve_suggestion',
    approvedFields,
    reviewNote,
    timestamp: serverTimestamp(),
  });
}

/**
 * Reject a suggestion
 */
export async function rejectSuggestion(
  suggestionId: string,
  moderatorId: string,
  reason: string
): Promise<void> {
  // Check if user is moderator
  const isModerator = await isUserModerator(moderatorId);
  if (!isModerator) {
    throw new Error('Nur Moderatoren koennen Vorschlaege ablehnen');
  }

  const suggestionRef = doc(db, SUGGESTIONS_COLLECTION, suggestionId);
  const suggestionSnap = await getDoc(suggestionRef);

  if (!suggestionSnap.exists()) {
    throw new Error('Vorschlag nicht gefunden');
  }

  const suggestion = suggestionSnap.data() as PlaceSuggestion;

  if (suggestion.status !== 'pending') {
    throw new Error('Vorschlag wurde bereits bearbeitet');
  }

  // Update suggestion status
  await updateDoc(suggestionRef, {
    status: 'rejected' as SuggestionStatus,
    reviewedBy: moderatorId,
    reviewedAt: serverTimestamp(),
    reviewNote: reason,
    updatedAt: serverTimestamp(),
  });

  // Log moderation action
  await addDoc(collection(db, MODERATION_ACTIONS_COLLECTION), {
    placeId: suggestion.placeId,
    suggestionId,
    moderatorId,
    action: 'reject_suggestion',
    reason,
    timestamp: serverTimestamp(),
  });
}

/**
 * Get the original place for comparison
 */
export async function getPlaceForSuggestion(
  placeId: string
): Promise<Place | null> {
  const docRef = doc(db, PLACES_COLLECTION, placeId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Place;
}

/**
 * Check if user has pending suggestion for a place
 */
export async function hasUserPendingSuggestion(
  userId: string,
  placeId: string
): Promise<boolean> {
  const q = query(
    collection(db, SUGGESTIONS_COLLECTION),
    where('suggestedBy', '==', userId),
    where('placeId', '==', placeId),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(q);
  return snapshot.size > 0;
}
